import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon, IosShare as IosShareIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from './ScrollableDialog';
import PrimaryActionButton from './PrimaryActionButton';
import { CUSTOMER_COLORS } from '../../constants/customerTheme';
import { IOS_ADD_TO_HOME_SCREEN_VIDEO_URL } from '../../constants/pwaBranding';
import useAddToHomeScreen from '../../hooks/useAddToHomeScreen';
import { useToast } from '../../context/ToastContext';
import { BRAND, BRAND_RADIUS } from '../../theme/brandTokens';

const AddToHomeScreenPrompt = ({
  scopeId,
  requireStoreHost = true,
  i18nPrefix = 'customer.addToHomeScreen',
  variant = 'customer',
}) => {
  const { t } = useTranslation();
  const { showInfo } = useToast();
  const {
    visible,
    platform,
    instructionsOpen,
    closeInstructions,
    startInstall,
    dismiss,
  } = useAddToHomeScreen({ scopeId, requireStoreHost });

  if (!visible) {
    return null;
  }

  const handleInstall = async () => {
    const result = await startInstall();
    if (result?.outcome === 'unavailable') {
      showInfo(t(`${i18nPrefix}.androidUnavailable`));
    }
  };

  const isManagement = variant === 'management';

  return (
    <>
      <Box
        sx={{
          background: isManagement ? BRAND.background : BRAND.surface,
          borderRadius: isManagement ? 0 : BRAND_RADIUS.card,
          boxShadow: 'none',
          border: isManagement ? 'none' : `1px solid ${BRAND.line}`,
          borderBottom: isManagement ? `1px solid ${BRAND.line}` : undefined,
          px: { xs: 1.5, sm: 2 },
          py: 1.25,
          mb: isManagement ? 0 : 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5 },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            flexShrink: 0,
            background: BRAND.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IosShareIcon sx={{ color: BRAND.onAccent, fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: '0.875rem', sm: '0.95rem' },
              color: CUSTOMER_COLORS.text,
              lineHeight: 1.25,
            }}
          >
            {t(`${i18nPrefix}.bannerTitle`)}
          </Typography>
          <Typography
            sx={{
              display: { xs: 'none', sm: 'block' },
              color: 'text.secondary',
              fontSize: '0.75rem',
              lineHeight: 1.3,
              mt: 0.25,
            }}
            noWrap
          >
            {t(`${i18nPrefix}.subtitle`)}
          </Typography>
        </Box>
        <PrimaryActionButton
          onClick={handleInstall}
          sx={{
            minHeight: 40,
            px: { xs: 1.5, sm: 2 },
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            flexShrink: 0,
            textTransform: 'none',
          }}
        >
          {t(`${i18nPrefix}.action`)}
        </PrimaryActionButton>
        <IconButton
          aria-label={t('common.close')}
          onClick={dismiss}
          size="small"
          sx={{ color: BRAND.textMuted, flexShrink: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <ScrollableDialog
        open={instructionsOpen}
        onClose={closeInstructions}
        maxWidth="sm"
        showCloseButton
        dividers
        title={t(`${i18nPrefix}.iosGuideTitle`)}
        titleSx={{ pb: 1, flexShrink: 0 }}
        contentSx={{ overflow: 'hidden' }}
        actionsSx={{ flexShrink: 0, justifyContent: 'flex-end' }}
        actions={(
          <PrimaryActionButton onClick={closeInstructions}>
            {t('common.close')}
          </PrimaryActionButton>
        )}
      >
        {platform === 'ios' && IOS_ADD_TO_HOME_SCREEN_VIDEO_URL ? (
          <Box
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: '#111',
              aspectRatio: '9 / 16',
              maxHeight: { xs: 420, sm: 520 },
              mx: 'auto',
            }}
          >
            <Box
              component="video"
              src={IOS_ADD_TO_HOME_SCREEN_VIDEO_URL}
              autoPlay
              muted
              loop
              playsInline
              controls={false}
              sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t(`${i18nPrefix}.iosFallback`)}
          </Typography>
        )}
      </ScrollableDialog>
    </>
  );
};

export default AddToHomeScreenPrompt;
